import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalculationResults } from '@/types/businesscase';
import { formatCurrency, formatPercentage, getROIColor } from '@/lib/businesscaseCalculations';
import { TrendingUp, TrendingDown, DollarSign, Calendar, Info, Download, CheckCircle, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useChartExport } from '@/hooks/useChartExport';
import { cn } from '@/lib/utils';
import { beoordeelBusinesscase, roiPeriodeLabel, OordeelNiveau } from '@/lib/businesscaseOordeel';

/**
 * De samenvatting is de enige plek op de pagina waar een oordeel in woorden
 * staat, en dat oordeel komt uit `beoordeelBusinesscase` (./oordeel) — dezelfde
 * functie die RiskIndicator gebruikt. Tot 8 september 2026 stond het oordeel in
 * ROIGauge, dat alleen `roiYear1` kent; dat leverde bij drie klantcases twee
 * tegengestelde teksten op één scherm op (docs/onderzoek/businesscase-tegenspraak.md).
 *
 * De getallen zelf zijn ongewijzigd: dit is een presentatiereparatie.
 */
const oordeelStijl: Record<OordeelNiveau, { bg: string; border: string; text: string; icon: string; Icon: typeof CheckCircle }> = {
  goed: {
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-800 dark:text-green-200',
    icon: 'text-green-600 dark:text-green-400',
    Icon: CheckCircle,
  },
  aandacht: {
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-800 dark:text-yellow-200',
    icon: 'text-yellow-600 dark:text-yellow-400',
    Icon: AlertTriangle,
  },
  slecht: {
    bg: 'bg-red-50 dark:bg-red-950',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-200',
    icon: 'text-red-600 dark:text-red-400',
    Icon: XCircle,
  },
  onbekend: {
    bg: 'bg-muted',
    border: 'border-border',
    text: 'text-muted-foreground',
    icon: 'text-muted-foreground',
    Icon: HelpCircle,
  },
};

interface BusinesscaseSummaryProps {
  calculations: CalculationResults;
  flexEEnabled: boolean;
  flexEValue: number;
  otherSubsidy?: number;
  businesscaseName?: string;
  roiMode?: 'bruto' | 'netto';
}

export function BusinesscaseSummary({ calculations, flexEEnabled, flexEValue, otherSubsidy = 0, businesscaseName, roiMode = 'bruto' }: BusinesscaseSummaryProps) {
  const { exportToPNG } = useChartExport();
  const {
    totalInvestment,
    eiaValue,
    netInvestment,
    roiYear1,
    roiBestCase,
    roiWorstCase,
    paybackPeriod,
    totalCashflow
  } = calculations;

  const roiColor = getROIColor(roiYear1);
  const roiMeetbaar = typeof roiYear1 === 'number' && Number.isFinite(roiYear1);
  const looptijdJaren = calculations.yearlyData?.length ?? 0;
  const oordeel = beoordeelBusinesscase({
    roiYear1,
    totalCashflow,
    paybackPeriod,
    roiMode,
    looptijdJaren,
  });
  const stijl = oordeelStijl[oordeel.niveau];
  const OordeelIcon = stijl.Icon;

  return (
    <Card className="border-border" id="financial-summary">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Financiële Samenvatting</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToPNG('financial-summary', 'financiele-samenvatting', businesscaseName)}
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total Investment */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Totale Investering</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-semibold">CAPEX (Capital Expenditure)</p>
                  <p className="text-xs mt-1">
                    Som van alle investeringskosten die in jaar 0 worden betaald
                  </p>
                  <p className="text-xs mt-1 text-muted-foreground">
                    = Batterij + Installatie + Software + Jaarlijks Abonnement (jaar 0) + Overige
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(totalInvestment)}</p>
        </div>

        {/* EIA Value */}
        {eiaValue > 0 && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300 mb-1">EIA Waarde</p>
            <p className="text-xl font-bold text-green-800 dark:text-green-200">
              -{formatCurrency(eiaValue)}
            </p>
          </div>
        )}

        {/* Flex-E Subsidy Card - Only show when enabled */}
        {flexEEnabled && flexEValue > 0 && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">Flex-E Subsidie</p>
            <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
              -{formatCurrency(flexEValue)}
            </p>
          </div>
        )}

        {/* Other Subsidies Card - Only show when > 0 */}
        {otherSubsidy > 0 && (
          <div className="p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-700 dark:text-purple-300 mb-1">Overige Subsidies</p>
            <p className="text-xl font-bold text-purple-800 dark:text-purple-200">
              -{formatCurrency(otherSubsidy)}
            </p>
          </div>
        )}

        {/* Net Investment */}
        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm text-primary">Netto Investering</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-primary/70 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-semibold">Netto CAPEX (na subsidies)</p>
                  <p className="text-xs mt-1">
                    Totale investering minus alle subsidies
                  </p>
                  <p className="text-xs mt-1 text-muted-foreground">
                    = CAPEX - EIA Waarde - Overige Subsidies
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-2xl font-bold text-primary">{formatCurrency(netInvestment)}</p>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-4" />

        {/* Het oordeel — het enige op deze pagina, en het weegt jaar 1 en de
            hele looptijd samen. Staat bewust bóven de losse getallen. */}
        <div className={cn('p-4 rounded-lg border', stijl.bg, stijl.border)}>
          <div className="flex items-center gap-2 mb-1">
            <OordeelIcon className={cn('h-4 w-4', stijl.icon)} />
            <p className={cn('text-sm font-semibold', stijl.text)}>{oordeel.kop}</p>
          </div>
          <p className="text-xs text-muted-foreground">{oordeel.uitleg}</p>
        </div>

        {/* ROI jaar 1 */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: `${roiColor}15` }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{roiPeriodeLabel(roiMode)}</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold">
                      {roiMode === 'netto' ? 'Netto ROI' : 'Bruto ROI'}
                    </p>
                    <p className="text-xs mt-1">
                      {roiMode === 'netto' 
                        ? 'Netto ROI: (Omzet - OPEX) / Netto Investering × 100%'
                        : 'Bruto ROI: Omzet / Netto Investering × 100%'
                      }
                    </p>
                    <p className="text-xs mt-2 text-muted-foreground">
                      {roiMode === 'netto'
                        ? 'Deze berekening houdt rekening met alle operationele kosten'
                        : 'Deze berekening kijkt alleen naar de bruto omzet'
                      }
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {/* Geen pijl bij een onbekend getal: `null >= 0` is false, wat een
                dalende pijl zou tekenen bij iets dat niet gemeten is. */}
            {!roiMeetbaar ? null : roiYear1 >= 0 ? (
              <TrendingUp className="h-4 w-4" style={{ color: roiColor }} />
            ) : (
              <TrendingDown className="h-4 w-4 text-destructive" />
            )}
          </div>
          <p className="text-3xl font-bold" style={{ color: roiColor }}>
            {formatPercentage(roiYear1)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {roiMeetbaar
              ? `${roiMode === 'netto' ? 'Netto berekening' : 'Bruto berekening'}, alleen het eerste jaar`
              : 'Niet te berekenen: de netto investering is nul of er zijn geen jaarrijen'}
          </p>
          {/* Het levensduurgetal staat hier, náást het jaar-1-getal, en niet pas
              onderaan de kolom. Een percentage over jaar 1 zonder de uitkomst
              over de looptijd ernaast nodigt uit tot doortrekken. */}
          <p className="text-xs mt-2">
            <span className="text-muted-foreground">
              {looptijdJaren > 0 ? `Over de looptijd (${looptijdJaren} jaar): ` : 'Over de looptijd: '}
            </span>
            <span className={cn('font-semibold', totalCashflow >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(totalCashflow)}
            </span>
          </p>
        </div>

        {/* Bandbreedte op jaar 1 (roiYear1 x 1,15 en x 0,90), geen scenario over
            de looptijd — zo heetten ze wel ("Best Case" / "Worst Case") en dat
            las als het laatste. */}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-xs text-green-700 dark:text-green-300 mb-1">Gunstig jaar 1 (+15%)</p>
            <p className="text-lg font-bold text-green-800 dark:text-green-200">
              {formatPercentage(roiBestCase)}
            </p>
          </div>
          <div className="p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
            <p className="text-xs text-orange-700 dark:text-orange-300 mb-1">Ongunstig jaar 1 (-10%)</p>
            <p className="text-lg font-bold text-orange-800 dark:text-orange-200">
              {formatPercentage(roiWorstCase)}
            </p>
          </div>
        </div>

        {/* Payback Period */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Terugverdientijd</p>
          </div>
          <p className="text-2xl font-bold">
            {paybackPeriod ? `${paybackPeriod} jaar` : 'N/A'}
          </p>
          {!paybackPeriod && (
            <p className="text-xs text-muted-foreground mt-1">
              Investering verdient zich niet terug binnen de periode
            </p>
          )}
        </div>

        {/* Total Cashflow */}
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Totale Cashflow</p>
          <p className={`text-2xl font-bold ${totalCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(totalCashflow)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
